import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, UserPlus, Calendar, Mail, Award } from 'lucide-react';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AdminNewSignups = ({ user }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNewSignups();
  }, []);

  const loadNewSignups = async () => {
    try {
      const response = await axios.get(`${API}/admin/client-analytics`, {
        headers: { 'admin-id': user.id }
      });
      
      // Sort by created_at (newest first) and take last 30 days
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const recentSignups = (response.data.clients || [])
        .filter(c => new Date(c.created_at) > thirtyDaysAgo)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setClients(recentSignups);
    } catch (error) {
      console.error('Error loading signups:', error);
      toast.error('Failed to load signups');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getRenewalDate = (createdAt) => {
    const created = new Date(createdAt);
    const renewal = new Date(created);
    renewal.setMonth(renewal.getMonth() + 1);
    return formatDate(renewal.toISOString());
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center"><p className="text-white">Loading...</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      <nav className="bg-[#1C1917] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-white/60" />
            </button>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-xl font-serif font-bold text-white">VowFlow</span>
            <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full ml-2">ADMIN</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">New Signups</h1>
          <p className="text-white/50">Recent clients who joined in the last 30 days</p>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-5xl font-serif font-light mb-2">{clients.length}</p>
              <p className="text-sm opacity-90">New Clients This Month</p>
            </div>
            <UserPlus size={48} className="opacity-50" />
          </div>
        </div>

        {clients.length === 0 ? (
          <div className="bg-[#1C1917] rounded-2xl p-12 text-center border border-white/10">
            <UserPlus className="mx-auto text-white/20 mb-4" size={48} />
            <p className="text-white/50">No new signups in the last 30 days</p>
          </div>
        ) : (
          <div className="bg-[#1C1917] rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr className="text-left text-white/50 text-sm">
                  <th className="px-6 py-3 font-medium">Couple</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Signup Date</th>
                  <th className="px-6 py-3 font-medium">Renewal Date</th>
                  <th className="px-6 py-3 font-medium">Wedding Date</th>
                  <th className="px-6 py-3 font-medium">Tier</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.client_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{client.couple_names}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white/60 text-sm">{client.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white/60 text-sm">{formatDate(client.created_at)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white/60 text-sm">{getRenewalDate(client.created_at)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white/60 text-sm">{formatDate(client.wedding_date)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs ${
                        client.subscription_tier === 'premium'
                          ? 'bg-purple-500/20 text-purple-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {client.subscription_tier === 'basic' ? 'Essentials' : 'Premium'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNewSignups;